import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, AlertIcon, Badge, Box, Button, FormControl, FormHelperText, FormLabel,
  HStack, IconButton, Input, Modal, ModalBody, ModalCloseButton, ModalContent,
  ModalFooter, ModalHeader, ModalOverlay, Radio, RadioGroup, Select, SimpleGrid,
  Switch, Table, TableContainer, Tbody, Td, Text, Th, Thead, Tr, VStack,
  useDisclosure, useToast,
} from '@chakra-ui/react';
import { AddIcon, DeleteIcon, EditIcon } from '@chakra-ui/icons';
import { useTranslation } from 'react-i18next';
import ConfirmModal from '../ConfirmModal';
import ratePlanService from '../../services/ratePlanService';
import {
  UNIT_SUFFIX, WEEKDAYS, apiError, money, rulePeriodLabel, weekdaySummary,
} from './format';

const EMPTY_RULE = {
  room_type: '', period: 'year', season: '', start_date: '', end_date: '',
  base_rate: '', min_los: '', max_los: '',
  adult_rate_adjustment: '', child_rate_adjustment: '', is_active: true,
  ...Object.fromEntries(WEEKDAYS.flatMap((d) => [
    [`${d.key}_adjustment_type`, ''], [`${d.key}_adjustment_value`, ''],
  ])),
};

const ruleToForm = (rule) => {
  if (!rule) return EMPTY_RULE;
  const period = rule.season ? 'season' : (rule.start_date ? 'dates' : 'year');
  const form = {
    room_type: rule.room_type || '',
    period,
    season: rule.season || '',
    start_date: rule.start_date || '',
    end_date: rule.end_date || '',
    base_rate: rule.base_rate ?? '',
    min_los: rule.min_los ?? '',
    max_los: rule.max_los ?? '',
    adult_rate_adjustment: rule.adult_rate_adjustment ?? '',
    child_rate_adjustment: rule.child_rate_adjustment ?? '',
    is_active: rule.is_active ?? true,
  };
  WEEKDAYS.forEach((d) => {
    form[`${d.key}_adjustment_type`] = rule[`${d.key}_adjustment_type`] || '';
    form[`${d.key}_adjustment_value`] = rule[`${d.key}_adjustment_value`] ?? '';
  });
  return form;
};

// format.js owns WEEKDAYS; translate a key if it carries one.
const optionLabel = (t, item) => (item.labelKey ? t(item.labelKey) : item.label);

const nullable = (v) => (v === '' || v === undefined ? null : v);

const formToPayload = (form, ratePlanId) => {
  const payload = {
    rate_plan: ratePlanId,
    room_type: nullable(form.room_type) && Number(form.room_type),
    season: form.period === 'season' ? Number(form.season) || null : null,
    start_date: form.period === 'dates' ? nullable(form.start_date) : null,
    end_date: form.period === 'dates' ? nullable(form.end_date) : null,
    base_rate: form.base_rate,
    min_los: nullable(form.min_los),
    max_los: nullable(form.max_los),
    adult_rate_adjustment: nullable(form.adult_rate_adjustment),
    child_rate_adjustment: nullable(form.child_rate_adjustment),
    is_active: form.is_active,
  };
  WEEKDAYS.forEach((d) => {
    const type = form[`${d.key}_adjustment_type`];
    const value = form[`${d.key}_adjustment_value`];
    const set = type && value !== '' && value !== null;
    payload[`${d.key}_adjustment_type`] = set ? type : null;
    payload[`${d.key}_adjustment_value`] = set ? value : null;
  });
  return payload;
};

const RuleModal = ({ isOpen, onClose, rule, plan, roomTypes, seasons, onSaved }) => {
  const [form, setForm] = useState(EMPTY_RULE);
  const [saving, setSaving] = useState(false);
  const toast = useToast();
  const { t } = useTranslation();

  useEffect(() => { if (isOpen) setForm(ruleToForm(rule)); }, [isOpen, rule]);

  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });
  const unit = UNIT_SUFFIX[plan.rate_unit] || t('ratePlanRules.labels.perNight');

  const periodValid = form.period === 'year'
    || (form.period === 'season' && form.season)
    || (form.period === 'dates' && form.start_date && form.end_date);
  const canSave = form.base_rate !== '' && periodValid;

  const save = async () => {
    setSaving(true);
    try {
      const payload = formToPayload(form, plan.id);
      if (rule) await ratePlanService.updateRule(rule.id, payload);
      else await ratePlanService.createRule(payload);
      toast({ title: rule ? t('ratePlanRules.toasts.ruleSaved') : t('ratePlanRules.toasts.ruleAdded'), status: 'success', duration: 2000 });
      onSaved();
      onClose();
    } catch (err) {
      toast({ title: t('ratePlanRules.toasts.saveFailed'), description: apiError(err), status: 'error', duration: 6000, isClosable: true });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{rule ? t('ratePlanRules.modal.editTitle') : t('ratePlanRules.modal.addTitle')}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack align="stretch" spacing={4}>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <FormControl>
                <FormLabel fontSize="sm">{t('ratePlanRules.labels.roomType')}</FormLabel>
                <Select size="sm" value={form.room_type} onChange={set('room_type')}>
                  <option value="">{t('ratePlanRules.labels.allRoomTypes')}</option>
                  {roomTypes.map((rt) => <option key={rt.id} value={rt.id}>{rt.name}</option>)}
                </Select>
              </FormControl>
              <FormControl isRequired>
                <FormLabel fontSize="sm">{t('ratePlanRules.labels.baseRateWithUnit', { unit })}</FormLabel>
                <Input size="sm" type="number" min={0} step="0.01"
                  value={form.base_rate} onChange={set('base_rate')} />
              </FormControl>
            </SimpleGrid>

            <FormControl>
              <FormLabel fontSize="sm">{t('ratePlanRules.labels.applies')}</FormLabel>
              <RadioGroup value={form.period} onChange={(v) => setForm({ ...form, period: v })}>
                <HStack spacing={5}>
                  <Radio size="sm" value="year">{t('ratePlanRules.labels.yearRound')}</Radio>
                  <Radio size="sm" value="season" isDisabled={seasons.length === 0}>
                    {t('ratePlanRules.labels.duringSeason')}
                  </Radio>
                  <Radio size="sm" value="dates">{t('ratePlanRules.labels.betweenDates')}</Radio>
                </HStack>
              </RadioGroup>
              {seasons.length === 0 && (
                <FormHelperText fontSize="xs">{t('ratePlanRules.labels.noSeasonsHelp')}</FormHelperText>
              )}
            </FormControl>

            {form.period === 'season' && (
              <FormControl isRequired>
                <FormLabel fontSize="sm">{t('ratePlanRules.labels.season')}</FormLabel>
                <Select size="sm" value={form.season} onChange={set('season')} placeholder={t('ratePlanRules.labels.pickSeason')}>
                  {seasons.map((s) => (
                    <option key={s.id} value={s.id}>
                      {t('ratePlanRules.labels.seasonOption', { name: s.name, start: s.start_date, end: s.end_date })}
                    </option>
                  ))}
                </Select>
              </FormControl>
            )}
            {form.period === 'dates' && (
              <SimpleGrid columns={2} spacing={4}>
                <FormControl isRequired>
                  <FormLabel fontSize="sm">{t('ratePlanRules.labels.from')}</FormLabel>
                  <Input size="sm" type="date" value={form.start_date} onChange={set('start_date')} />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel fontSize="sm">{t('ratePlanRules.labels.toInclusive')}</FormLabel>
                  <Input size="sm" type="date" value={form.end_date} onChange={set('end_date')} />
                </FormControl>
              </SimpleGrid>
            )}

            <Box>
              <Text fontSize="sm" fontWeight="600" mb={1}>{t('ratePlanRules.labels.weekdayAdjustments')}</Text>
              <Text fontSize="xs" color="gray.500" mb={2}>
                {t('ratePlanRules.labels.weekdayHelp')}
              </Text>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacingX={6} spacingY={2}>
                {WEEKDAYS.map((d) => (
                  <HStack key={d.key} spacing={2}>
                    <Text fontSize="sm" w="36px">{optionLabel(t, d)}</Text>
                    <Select size="sm" w="130px" value={form[`${d.key}_adjustment_type`]}
                      onChange={set(`${d.key}_adjustment_type`)}>
                      <option value="">{t('ratePlanRules.labels.noChange')}</option>
                      <option value="percentage">{t('ratePlanRules.labels.percent')}</option>
                      <option value="fixed">{t('ratePlanRules.labels.amount')}</option>
                    </Select>
                    <Input size="sm" type="number" step="0.01" w="110px"
                      isDisabled={!form[`${d.key}_adjustment_type`]}
                      value={form[`${d.key}_adjustment_value`]}
                      onChange={set(`${d.key}_adjustment_value`)}
                      placeholder={form[`${d.key}_adjustment_type`] === 'percentage' ? '%' : t('ratePlanRules.labels.amountPlaceholder')} />
                  </HStack>
                ))}
              </SimpleGrid>
            </Box>

            <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
              <FormControl>
                <FormLabel fontSize="sm">{t('ratePlanRules.labels.minStay')}</FormLabel>
                <Input size="sm" type="number" min={1} value={form.min_los} onChange={set('min_los')} placeholder={t('ratePlanRules.labels.none')} />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm">{t('ratePlanRules.labels.maxStay')}</FormLabel>
                <Input size="sm" type="number" min={1} value={form.max_los} onChange={set('max_los')} placeholder={t('ratePlanRules.labels.none')} />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm">{t('ratePlanRules.labels.extraAdult')}</FormLabel>
                <Input size="sm" type="number" min={0} step="0.01" value={form.adult_rate_adjustment}
                  onChange={set('adult_rate_adjustment')} placeholder="0.00" />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm">{t('ratePlanRules.labels.extraChild')}</FormLabel>
                <Input size="sm" type="number" min={0} step="0.01" value={form.child_rate_adjustment}
                  onChange={set('child_rate_adjustment')} placeholder="0.00" />
              </FormControl>
            </SimpleGrid>
            <Text fontSize="xs" color="gray.500" mt={-2}>
              {t('ratePlanRules.labels.extraGuestHelp')}
            </Text>

            <FormControl display="flex" alignItems="center">
              <Switch size="sm" mr={2} isChecked={form.is_active}
                onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
              <FormLabel fontSize="sm" mb={0}>{t('ratePlanRules.labels.active')}</FormLabel>
            </FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>{t('ratePlanRules.buttons.cancel')}</Button>
          <Button colorScheme="blue" onClick={save} isLoading={saving} isDisabled={!canSave}>
            {rule ? t('ratePlanRules.buttons.save') : t('ratePlanRules.buttons.addRule')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

// Rough specificity rank that mirrors the engine's priority, used to
// sort the table so the rule that wins appears first in each group.
const specificity = (r) => (r.room_type ? 100 : 0)
  + (r.start_date ? 3 : r.season ? 2 : 1);

const RulesPanel = ({ plan, roomTypes, seasons }) => {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const modal = useDisclosure();
  const confirm = useDisclosure();
  const toast = useToast();
  const { t } = useTranslation();

  const seasonsById = useMemo(
    () => Object.fromEntries(seasons.map((s) => [s.id, s])), [seasons]);
  const roomTypeName = useMemo(
    () => Object.fromEntries(roomTypes.map((rt) => [rt.id, rt.name])), [roomTypes]);

  const load = async () => {
    setLoading(true);
    try {
      setRules(await ratePlanService.getRules(plan.id));
    } catch (err) {
      toast({ title: t('ratePlanRules.toasts.loadFailed'), description: apiError(err), status: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [plan.id]);

  const sorted = [...rules].sort((a, b) => {
    const rtA = a.room_type ? (roomTypeName[a.room_type] || '') : '';
    const rtB = b.room_type ? (roomTypeName[b.room_type] || '') : '';
    return rtA.localeCompare(rtB) || specificity(b) - specificity(a);
  });

  const unpricedRoomTypes = roomTypes.filter((rt) => !rules.some(
    (r) => r.is_active && (r.room_type === rt.id || !r.room_type)));

  const remove = async () => {
    try {
      await ratePlanService.removeRule(deleteTarget.id);
      toast({ title: t('ratePlanRules.toasts.ruleDeleted'), status: 'success', duration: 2000 });
      confirm.onClose();
      load();
    } catch (err) {
      toast({ title: t('ratePlanRules.toasts.deleteFailed'), description: apiError(err), status: 'error' });
    }
  };

  const unit = UNIT_SUFFIX[plan.rate_unit] || t('ratePlanRules.labels.perNight');

  const stayLimits = (r) => {
    if (r.min_los && r.max_los) return t('ratePlanRules.table.stayMinMax', { min: r.min_los, max: r.max_los });
    if (r.min_los) return t('ratePlanRules.table.stayMin', { min: r.min_los });
    if (r.max_los) return t('ratePlanRules.table.stayMax', { max: r.max_los });
    return '—';
  };

  const extraGuests = (r) => {
    const adult = r.adult_rate_adjustment ? money(r.adult_rate_adjustment) : null;
    const child = r.child_rate_adjustment ? money(r.child_rate_adjustment) : null;
    if (adult && child) return t('ratePlanRules.table.extraAdultChild', { adult, child });
    if (adult) return t('ratePlanRules.table.extraAdult', { adult });
    if (child) return t('ratePlanRules.table.extraChild', { child });
    return '—';
  };

  return (
    <VStack align="stretch" spacing={3}>
      <HStack justify="space-between" align="flex-start">
        <Text fontSize="xs" color="gray.600" maxW="640px">
          {t('ratePlanRules.panel.priorityHelp')}
        </Text>
        <Button size="sm" leftIcon={<AddIcon />} colorScheme="blue"
          onClick={() => { setEditing(null); modal.onOpen(); }}>
          {t('ratePlanRules.buttons.addRule')}
        </Button>
      </HStack>

      {!loading && unpricedRoomTypes.length > 0 && (
        <Alert status="warning" borderRadius="md" py={2} fontSize="sm">
          <AlertIcon />
          {t('ratePlanRules.panel.unpricedWarning', { roomTypes: unpricedRoomTypes.map((rt) => rt.name).join(', ') })}
        </Alert>
      )}

      <TableContainer borderWidth="1px" borderRadius="md" bg="white" whiteSpace="normal">
        <Table size="sm">
          <Thead bg="gray.50">
            <Tr>
              <Th>{t('ratePlanRules.labels.roomType')}</Th>
              <Th>{t('ratePlanRules.labels.applies')}</Th>
              <Th isNumeric>{t('ratePlanRules.table.baseRate')}</Th>
              <Th>{t('ratePlanRules.table.weekdayChanges')}</Th>
              <Th>{t('ratePlanRules.table.stayLimits')}</Th>
              <Th>{t('ratePlanRules.table.extraGuests')}</Th>
              <Th />
            </Tr>
          </Thead>
          <Tbody>
            {loading && (
              <Tr><Td colSpan={7}><Text fontSize="sm" color="gray.500">{t('ratePlanRules.table.loading')}</Text></Td></Tr>
            )}
            {!loading && sorted.length === 0 && (
              <Tr><Td colSpan={7}>
                <Text fontSize="sm" color="gray.500" py={2}>
                  {t('ratePlanRules.emptyState.noRules')}
                </Text>
              </Td></Tr>
            )}
            {sorted.map((r) => (
              <Tr key={r.id} opacity={r.is_active ? 1 : 0.5}>
                <Td fontSize="sm">
                  {r.room_type ? (roomTypeName[r.room_type] || r.room_type_name) : (
                    <Text as="span" color="gray.600">{t('ratePlanRules.labels.allRoomTypes')}</Text>
                  )}
                  {!r.is_active && <Badge ml={2} fontSize="2xs">{t('ratePlanRules.table.inactive')}</Badge>}
                </Td>
                <Td fontSize="sm">{rulePeriodLabel(r, seasonsById)}</Td>
                <Td isNumeric fontSize="sm" fontWeight="600">
                  {money(r.base_rate)} <Text as="span" fontSize="xs" color="gray.500">{unit}</Text>
                </Td>
                <Td fontSize="xs" whiteSpace="normal">{weekdaySummary(r) || '—'}</Td>
                <Td fontSize="xs">{stayLimits(r)}</Td>
                <Td fontSize="xs">{extraGuests(r)}</Td>
                <Td>
                  <HStack spacing={1} justify="flex-end">
                    <IconButton size="xs" variant="ghost" icon={<EditIcon />} aria-label={t('ratePlanRules.table.editRule')}
                      onClick={() => { setEditing(r); modal.onOpen(); }} />
                    <IconButton size="xs" variant="ghost" colorScheme="red" icon={<DeleteIcon />}
                      aria-label={t('ratePlanRules.table.deleteRule')} onClick={() => { setDeleteTarget(r); confirm.onOpen(); }} />
                  </HStack>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </TableContainer>

      <RuleModal isOpen={modal.isOpen} onClose={modal.onClose} rule={editing} plan={plan}
        roomTypes={roomTypes} seasons={seasons} onSaved={load} />
      <ConfirmModal isOpen={confirm.isOpen} onClose={confirm.onClose} onConfirm={remove}
        title={t('ratePlanRules.confirm.deleteTitle')} confirmLabel={t('ratePlanRules.confirm.deleteLabel')}
        message={t('ratePlanRules.confirm.deleteMessage')} />
    </VStack>
  );
};

export default RulesPanel;
